<template>
  <div id="user-guide" class="fill-height align-start">
    <v-row class="g-container">
      <v-col cols="12" md="3">
        <v-card class="p-table-content-card">
          <v-card-title>
            <h3>Table of Contents</h3>
          </v-card-title>
          <v-card-text>
            <user-guide-table-of-contents :sections="sections" />
          </v-card-text>
        </v-card>
      </v-col>

      <v-col class="px-md-16" cols="12" md="9">
        <h2>User Guide Supplier Portal</h2>
        <p>
          Designed to assist you with getting the most out of your supplier
          portal
        </p>

        <v-card v-if="!userGuideRead" class="mt-7 mb-2 px-4 p-user-guide-card">
          <div class="d-flex align-center my-0 py-4">
            <label
              class="p-user-guide-acknowledgement g-text-color-black g-text-l"
            >
              <input
                v-model="tickUserGuideRead"
                type="checkbox"
                class="p-user-guide-checkbox"
              />
              <span>
                I have read the <span class="g-text-l g-bold">User Guide</span>.
              </span>
            </label>
            <v-spacer />
            <v-btn
              large
              class="i-btn-block-primary"
              :disabled="!tickUserGuideRead"
              :loading="updating"
              @click="confirmUserGuideRead()"
            >
              Confirm
            </v-btn>
          </div>
        </v-card>

        <v-alert v-if="loading" type="info" text>
          Loading user guide...
        </v-alert>

        <v-alert v-else-if="!sections.length" type="info" text>
          No user guide content is available yet for this tenant.
        </v-alert>

        <div v-else class="p-section-main">
          <user-guide-section-tree
            :sections="sections"
            :render-content="renderSectionContent"
          />
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import { updateProfile } from '@/services/profile'
import { fetchUserGuide } from '@/services/user-guide'
import UserGuideSectionTree from '../components/UserGuideSectionTree'
import UserGuideTableOfContents from '../components/UserGuideTableOfContents'

export default {
  components: {
    UserGuideSectionTree,
    UserGuideTableOfContents
  },

  data: () => ({
    tickUserGuideRead: false,
    updating: false,
    loading: false,
    sections: []
  }),

  async created() {
    if (!this.userGuideEnabled) {
      this.$router.replace({ name: 'dashboard' })
      return
    }
    await this.loadUserGuide()
  },

  computed: {
    ...mapGetters('profile', ['userGuideRead']),
    ...mapGetters('tenant', ['taxDisplayPolicy', 'userGuideEnabled']),
    ...mapState('preferences', ['selectedSupplierId'])
  },

  methods: {
    ...mapActions('profile', ['fetchSupplierProfile']),
    ...mapActions('notifications', ['addNotification']),

    async loadUserGuide() {
      this.loading = true
      try {
        this.sections = await fetchUserGuide()
      } catch (error) {
        this.addNotification({
          color: 'error',
          text: 'User Guide could not be loaded, please try again'
        })
      } finally {
        this.loading = false
      }
    },

    renderSectionContent(html) {
      const taxCodeGuideText = this.taxDisplayPolicy.showWet
        ? `Confirm applicable Tax Codes and ${this.taxDisplayPolicy.wetLabel} where applicable (liquor only)`
        : 'Confirm applicable Tax Codes where applicable'

      return (html || '')
        .replaceAll('[[TAX_CODE_GUIDE_TEXT]]', taxCodeGuideText)
        .replaceAll(
          'support@flewidtech.com',
          '<a href="mailto:support@flewidtech.com">support@flewidtech.com</a>'
        )
    },

    confirmUserGuideRead() {
      this.updating = true

      updateProfile(this.selectedSupplierId, { userGuideRead: true })
        .then(() => {
          this.addNotification({
            color: 'secondary',
            text: 'Confirmation successfully completed'
          })
          this.fetchSupplierProfile(this.selectedSupplierId)
          this.$router.push({
            name: 'dashboard',
            query: { showTradingSteps: 'true' }
          })
        })
        .catch(() => {
          this.addNotification({
            color: 'error',
            text: 'Confirmation could not be processed, please try again'
          })
        })
        .finally(() => {
          this.updating = false
        })
    }
  }
}
</script>

<style lang="scss" scoped>
#user-guide {
  scroll-behavior: smooth;

  .p-user-guide-acknowledgement {
    align-items: center;
    cursor: pointer;
    display: inline-flex;
  }

  .p-user-guide-checkbox {
    accent-color: $primary-color;
    cursor: pointer;
    flex-shrink: 0;
    height: 18px;
    margin-right: 8px;
    width: 18px;
  }

  .p-user-guide-card {
    border: 3px solid $primary-color;
  }
}
</style>
